import {
  Box,
  ExpandableSection,
  ExpandableSectionProps,
  Heading,
  smBumps,
  xsBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import { getActiveCoverLevelOption, Quote } from '../../../domain'
import { useConditionalCovers, useIncludedCovers } from '../../../hooks/covers'
import {
  tProduct,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { getExposuresContentBaseUrl, useConfig } from '../../../state/configuration'
import { useProduct } from '../../product/ProductProvider'
import { ConditionalCoverItem } from './ConditionalCoverItem'
import { CoverEndorsements } from './CoverEndorsements'
import { IncludedCoverItem } from './IncludedCoverItem'

export interface IncludedCoversExpandableProps extends ExpandableSectionProps {
  quote: Quote
}

export const IncludedCoversExpandable: FunctionComponent<
  IncludedCoversExpandableProps
> = ({ quote, header, ...props }) => {
  const { t } = useWriskTranslation()

  const { scheme, product } = useProduct()

  const coverLevelOption = getActiveCoverLevelOption(quote)

  const includedCovers = useIncludedCovers(product, coverLevelOption)

  const conditionalCovers = useConditionalCovers(quote, product)
  const contentBaseUrl = useConfig(getExposuresContentBaseUrl(scheme.schemeCode))

  const includedCoverItems = includedCovers?.flatMap((cover, index, covers) => {
    if (typeof cover === 'string') {
      return (
        <IncludedCoverItem
          key={cover}
          cover={cover}
          width={1}
          contentBaseUrl={contentBaseUrl}
          typoSize='sm'
          iconSize='small'
        />
      )
    } else {
      return [
        <Heading px={smBumps} py={xsBumps} key={cover.header}>
          {t(tProduct('cover-levels', cover.header, 'header'))}
        </Heading>,
        ...cover.items.map((item) => (
          <IncludedCoverItem
            key={`${cover.header}-${item}`}
            cover={item}
            width={1}
            contentBaseUrl={contentBaseUrl}
            typoSize='sm'
            iconSize='small'
          />
        )),
        index < covers.length - 1 ? (
          <Box my={2} px={smBumps} width={1} key={`${cover.header}-divider`}>
            <Box height={0} borderTopWidth={1}>
              &nbsp;
            </Box>
          </Box>
        ) : null,
      ]
    }
  })

  return (
    <ExpandableSection header={header} {...props}>
      {includedCoverItems}
      {conditionalCovers.map((it) => (
        <ConditionalCoverItem
          key={it.coverCode}
          conditionalCover={it}
          width={1}
          typoSize='sm'
          iconSize='small'
        />
      ))}
      <CoverEndorsements quote={quote} my={3} />
    </ExpandableSection>
  )
}
